/**
 * priorYearOverlay (#2373) — day-of-season alignment for the −1/−2/−3-Jahr
 * prior-year Ist overlays.
 *
 * The lens now selects a Kultur·Sorte GROUP spanning multiple crop_years, so a
 * prior season's cumulative harvest can be drawn against the current one as a
 * "same point of the season" comparison. Each prior-season pick is shifted
 * forward by its season delta (``currentSeason − memberCropYear`` = the offset)
 * so a pick on 2024-08-15 (Saison 2024) lands on 2026-08-15 against Saison 2026;
 * the shifted per-day sums are then cumulated by ``buildOutputChart`` exactly
 * like the current Ist line. Pure + unit-tested (no React/recharts/network).
 *
 * ``crop_year`` is the HARVEST SEASON — the calendar year the crop is harvested
 * in — which is what the wiberry/myfarm producers write and what Deckungsbeitrag
 * labels its periods with. (An earlier version of this docblock asserted it was
 * the Wirtschaftsjahr START year. That was FALSE, and #2610 is what it cost: for
 * any crop whose harvest ends before ``wj_start_month`` the two readings sit a
 * permanent year apart, so WF Spargel's Mar–Jun season was reachable at no
 * wall-clock date at all.) Grouping by season is also what makes the shift safe:
 * a pick belongs to exactly one cycle and a cycle to exactly one ``crop_year``,
 * so seasons PARTITION the picks — a season that straddles the WJ boundary
 * (2022 Spargel, Feb–Nov) lands whole in its own offset and can never
 * double-count into a neighbouring one. The shift stays a whole-year add,
 * keeping the day-of-season fixed.
 */
import type { PriorYearSeries } from './outputSeries';

/** The prior-year offsets the overlay supports (−1/−2/−3 Jahr). */
const MAX_OFFSET = 3;

/**
 * Shift an ISO date forward by ``years`` calendar years, keeping month + day.
 * Feb 29 clamps to Feb 28 when the target year is not a leap year (so a shift
 * never silently rolls into March).
 */
export function shiftYears(dateIso: string, years: number): string {
  const [y, m, d] = dateIso.slice(0, 10).split('-').map(Number);
  const ny = y + years;
  const lastDay = new Date(Date.UTC(ny, m, 0)).getUTCDate(); // day 0 of next month = last of this
  const day = Math.min(d, lastDay);
  return `${ny}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Concrete-year label for a prior-season overlay toggle (#2436): the season the
 * offset represents, DERIVED from the current season so it rolls forward each
 * year. With Saison 2026 current, offset 1/2/3 → ``"2025"``/``"2024"``/``"2023"``
 * — the chips ARE crop_year groups. Replaces the old relative "−N Jahr" label.
 */
export function priorYearLabel(currentSeason: number, offset: number): string {
  return String(currentSeason - offset);
}

/**
 * Group prior-season picks into one overlay series per offset (1/2/3), shifting
 * each pick's date forward by the offset and summing per shifted day. Picks of
 * cycles in the current season (offset 0), beyond −3, or of unknown cycles are
 * dropped. Returns offsets ascending; each series' points sorted by (shifted)
 * date. ``buildOutputChart`` cumulates + clamps them onto the axis.
 */
export function buildPriorYearSeries(
  priorPicks: { cropCycleId: string | null; date: string; qty: number }[],
  cropYearByCycle: Record<string, number>,
  currentSeason: number,
): PriorYearSeries[] {
  const byOffset = new Map<number, Map<string, number>>();
  for (const p of priorPicks) {
    const cy = p.cropCycleId ? cropYearByCycle[p.cropCycleId] : undefined;
    if (cy == null) continue;
    const offset = currentSeason - cy;
    if (offset < 1 || offset > MAX_OFFSET) continue;
    const shifted = shiftYears(p.date, offset);
    const perDay = byOffset.get(offset) ?? new Map<string, number>();
    perDay.set(shifted, (perDay.get(shifted) ?? 0) + p.qty);
    byOffset.set(offset, perDay);
  }
  return [...byOffset.entries()]
    .sort(([a], [b]) => a - b)
    .map(([offset, perDay]) => ({
      offset,
      points: [...perDay.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({ date, value })),
    }));
}
